/* eslint-disable max-statements */
/* eslint-disable max-len */
import {useEffect, useRef, useState} from 'react';
import classNames from 'classnames';
import {Popper, PopperTrigger} from '@baidu/luffy-comp-popper';

import sdk from '@/sdk';
import {DocMembers, Member} from '@/interface';
import {MemberType, DocRole, Operation, Role, ApplyRole} from '@/Constant';
import {ServerConfig} from '@/config/routers';
import Checkbox from '@/components/Checkbox';
import Avatar, {AvatarSize} from '@/components/Avatar';
import DropMenu, {DropMenuTrigger, DropMenuValue} from '@/components/DropMenu';
import Icon, {IconSize} from '@/components/Icon';
import toast, {ToastType} from '@/components/Toast';
import {NoPermissionsSvg, ArrowDownSvg} from '@/icons';
import {reportHoverShowApplyPermission} from '@/buried/aigc';

import styles from './index.module.scss';
import {feLogger, knowledgeSendApply, permissionApply} from '@/buried';
import {selectAdmin, CUR_USER_POWER, applyPermissionPage, APP_CONT, APP_TYPE, APP_CHANNEL} from '@/hooks/permission';

interface CheckboxItem {
    id: string;
    name: string;
    email?: string;
    checked?: boolean;
    role?: string;
    recommend?: boolean;
}

interface DocInfo {
    spaceGuid: string;
    groupGuid: string;
    repositoryGuid: string;
}

interface IProps {
    docGuid: string;
}

const noAdminSolveAddress = 'https://ku.baidu-int.com/knowledge/HFVrC7hq1Q/pKzJfZczuc/munIPCvcFy/EysdfehJN0gleV';

function getRoleName(role: string) {
    switch (role) {
        case Role.Owner:
            return '创建者';
        case Role.Admin:
            return '知识库管理员';
        case Role.Member:
            return '知识库成员';
        case Role.DocAdmin:
            return '页面管理员';
        case ApplyRole.DocEditor:
            return '最近编辑者';
        default:
            return '';
    }
}

const ApplyPermission: React.FC<IProps> = ({docGuid}) => {
    const [docInfo, setDocInfo] = useState<DocInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true); // 加载状态

    const [adminList, setAdminList] = useState<CheckboxItem[]>([]);
    const selectedAdminList = adminList.filter(item => item.checked);
    const owner = adminList.find(item => item.role === Role.Owner);
    const docAdmin = adminList.find(item => item.role === Role.DocAdmin);

    useEffect(() => {
        sdk.as.getDocInfoById(docGuid).then(result => {
            const docInfo = {
                spaceGuid: result.spaceGuid,
                groupGuid: result.groupGuid,
                repositoryGuid: result.repositoryGuid,
            };
            setDocInfo(docInfo);
        }).catch(err => {
            console.error(err);
        });
    }, [docGuid]);

    const permissionOptions = [
        {
            label: '页面管理员',
            value: 0,
            role: DocRole.DocAdmin,
        },
        {
            label: '可编辑',
            value: 1,
            role: DocRole.Member,
        },
        {
            label: '可阅读',
            value: 2,
            role: DocRole.Reader,
        },
    ];

    const [menuPopperVisible, setMenuPopperVisible] = useState(false);
    const onChangeMenuPopperVisible = (val: boolean) => {
        setMenuPopperVisible(val);
    };
    useEffect(() => {
        // 展示权限申请，埋点上报
        reportHoverShowApplyPermission({
            mentionDocId: docGuid,
        });

        return () => {
            setMenuPopperVisible(false);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [currentOption, setCurrentOption] = useState(permissionOptions[2]);
    const [isApplied, setIsApplied] = useState(false);

    const [popperVisible, setPopperVisible] = useState(false);

    async function getDocAdminList(docGuid: string, docInfo: DocInfo, selectRole?: string) {
        let list: CheckboxItem[] = [];
        const ksServer = await sdk.getKsServer({
            spaceGuid: docInfo.spaceGuid,
            groupGuid: docInfo.groupGuid,
            repositoryGuid: docInfo.repositoryGuid,
        });
        await ksServer.getDocAdmin(docGuid, selectRole || currentOption.role).then(result => {
            list = (result || []).map((item: any) => ({
                id: item.memberGuid,
                name: item.nickname,
                checked: false,
                role: item.role,
                recommend: item.recommend,
            }));
            setAdminList(list);
            return sdk.as.getUserInfoList(list.map((item: any) => item.id));
        }).then(result => {
            const userMap: {[key: string]: User} = {};
            result.forEach(item => {
                userMap[item.userGuid] = item;
            });
            list.forEach(item => {
                item.email = userMap[item.id]?.email || '';
                item.checked = list[0].id === item.id;
            });
            setAdminList([...list]);
        }).finally(() => {
            setIsLoading(false); // 数据加载完成
        });
    }

    const handleClickMenu = (val: DropMenuValue) => {
        const option = permissionOptions[val as number];
        setCurrentOption(option);
        if (val !== currentOption.value) {
            setAdminList(adminList.map(item => {
                item.checked = false;
                return item;
            }));
            docInfo && getDocAdminList(docGuid, docInfo, option.role);
        }
    };

    useEffect(() => {
        docInfo && getDocAdminList(docGuid, docInfo, currentOption.role);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docGuid, docInfo]);

    const onCheckboxChange = (id: string) => {
        adminList.forEach(item => {
            item.checked = item.id === id; // 只勾选一个
        });
        setAdminList([...adminList]);
    };

    const onPopperChange = (val: boolean) => {
        if (val) {
            if (selectedAdminList.length === 0) {
                if (owner) {
                    onCheckboxChange(owner.id); // 创建者
                } else if (docAdmin) {
                    onCheckboxChange(docAdmin.id); // 页面管理员
                }
            }
        }
        setPopperVisible(val);
    };

    const sendApply = async () => {
        if (!docInfo) {
            return;
        }
        if (selectedAdminList.length === 0) {
            const defaultAdmin = owner || docAdmin; // 优先选择创建者，没有再选择页面管理员
            if (defaultAdmin) {
                adminList.forEach(item => {
                    item.checked = item.id === defaultAdmin.id;
                });
                setAdminList([...adminList]);
            }
        }
        // 重新选中的管理员
        const updatedSelectedAdminList = adminList.filter(item => item.checked);
        // 是否选择了管理员
        if (updatedSelectedAdminList.length === 0) {
            toast({
                type: ToastType.Error,
                content: '管理员为必选项！',
            });
            return;
        }
        const {spaceGuid, groupGuid, repositoryGuid} = docInfo;
        const ksServer = await sdk.getKsServer({spaceGuid, groupGuid, repositoryGuid});
        const member: Member = {
            memberGuid: sdk.as.user?.userGuid as string,
            memberType: MemberType.User,
            nickname: sdk.as.user?.nickname as string,
        };
        const docMembers: DocMembers = {
            members: [member],
            action: Operation.Apply,
            roleName: currentOption.role,
            message: '',
        };
        if (updatedSelectedAdminList.length > 0) {
            docMembers.receiverGuid = updatedSelectedAdminList[0]?.id;
        }
        ksServer.addDocMember(docGuid, docMembers, true).then(result => {
            if (updatedSelectedAdminList.length > 0) {
                const {memberEvents} = result;
                const {eventGuid} = memberEvents[0];
                const selected = updatedSelectedAdminList[0];
                return sdk.as.sendAdminMessagesSingle({
                    message: '',
                    roleName: currentOption.role,
                    spaceGuid,
                    groupGuid,
                    repositoryGuid,
                    docGuid,
                    memberEvent: {
                        eventGuid,
                        memberGuid: sdk.user?.userGuid || '',
                        receiverGuid: selected?.id,
                    },
                });
            }
            feLogger(permissionApply({
                app_cont: APP_CONT.Doc,
                app_channel: APP_CHANNEL.repoNoPermission, // 知识库内无权链接权限申请
                app_power: applyPermissionPage(currentOption.role),
                apper_power: CUR_USER_POWER.noAuth,
                slelct_admin: selectAdmin(updatedSelectedAdminList.length > 0 ? updatedSelectedAdminList[0]?.role : undefined),
                time: +new Date(),
                user_id: member.memberGuid,
                app_type: APP_TYPE.Document,
                doc_id: docGuid,
                admin_id: updatedSelectedAdminList.length > 0 ? updatedSelectedAdminList[0]?.memberGuid : undefined,
                event_id: result?.memberEvents[0]?.eventGuid || '',
            }));
        }).then(() => {
            setIsApplied(true);
        }).catch(() => {
            toast({
                type: ToastType.Error,
                content: '发送申请失败',
            });
        });
    };

    const getAdminLabel = (admins: CheckboxItem) =>
        (admins ? `${admins.name}（${getRoleName(admins?.role ?? '')}）` : '申请对象');

    const getSelectedAdminLabel = (admins: CheckboxItem[]) => {
        if (admins.length === 0) {
            return null;
        }

        if (admins.length === 1) {
            return (
                <span className={styles.label}>
                    {`${admins[0].name}（${getRoleName(admins[0].role ?? '')}）`}
                </span>
            );
        }

        return (
            <>
                <span className={styles.label}>
                    {`${admins[0].name}（${getRoleName(admins[0].role ?? '')}）`}
                </span>
                ...等{admins.length - 1}人
            </>
        );
    };

    const selectLabel = selectedAdminList.length === 0
        ? (
            owner ? (
                <span className={styles.label}>
                    {getAdminLabel(owner)}
                </span>
            ) : (
                <span className={styles.label}>
                    {getAdminLabel(docAdmin as CheckboxItem ?? '')}
                </span>
            )
        ) : getSelectedAdminLabel(selectedAdminList);


    if (!docInfo) {
        return null;
    }

    const handleClick = () => {
        window.open(noAdminSolveAddress);
    };

    if (isLoading) {
        return null;
    }

    return <div className={styles.permission}>
        <Icon svg={NoPermissionsSvg} size={IconSize.Auto} className={styles.mainIcon} />
        {
            adminList.length === 0 ? (
                <div className={styles.noAdmin}>
                    <span>无该页面权限，当前页面暂无有效管理员。</span>
                    <span
                        className={styles.jumpAddress}
                        onClick={handleClick}
                    >点击查看如何申请权限</span>
                </div>
            ) : (
                <>
                    {isApplied
                        ? <div className={styles.title}>已申请权限</div>
                        : <>
                            <div className={styles.title}>无该文档权限</div>
                            <div className={styles.main}>
                                您可向
                                <PopperTrigger
                                    onChange={onPopperChange}
                                >
                                    <button className={styles.menuBtn}>
                                        {selectLabel}
                                        <Icon
                                            svg={ArrowDownSvg}
                                            className={classNames(styles.iconArrowDown, popperVisible && styles.expand)}
                                        />
                                    </button>
                                    <Popper
                                        className={styles.popperWrap}
                                        placement={'bottom'}
                                    >
                                        <div className={styles.popperContent}>
                                            <div className={styles.tipsText}>将给选择的申请对象发送如流通知</div>
                                            {adminList.length === 0 && <div className={styles.noData}>无管理员</div>}
                                            {adminList.map((item) => {
                                                return <div
                                                    key={item.id}
                                                    className={styles.adminItem}
                                                    onClick={() => onCheckboxChange(item.id)}
                                                >
                                                    <Checkbox
                                                        className={styles.checkbox}
                                                        size={'sm'}
                                                        checked={item.checked}
                                                    />
                                                    <Avatar
                                                        className={styles.avatar}
                                                        size={AvatarSize.SM}
                                                        userGuid={item.id}
                                                    />
                                                    <div className={styles.userInfo}>
                                                        <div className={styles.name}>
                                                            {item.name}（{getRoleName(item.role ?? '')}）
                                                        </div>
                                                        <div className={styles.email}>{item.email}</div>
                                                    </div>
                                                </div>;
                                            })}
                                        </div>
                                    </Popper>
                                </PopperTrigger>
                                <span className={styles.applyText}>申请</span>
                                <DropMenu
                                    options={permissionOptions}
                                    value={currentOption.value}
                                    triggerType={DropMenuTrigger.Click}
                                    menuPopperVisible={menuPopperVisible}
                                    onChangeMenuPopperVisible={onChangeMenuPopperVisible}
                                    onChange={handleClickMenu}
                                    placement={'bottom-start'}
                                >
                                    <button className={styles.menuBtn}>
                                        {currentOption.label}
                                        <Icon
                                            svg={ArrowDownSvg}
                                            className={classNames(styles.iconArrowDown, menuPopperVisible && styles.expand)}
                                        />
                                    </button>
                                </DropMenu>
                            </div>
                            <div className={styles.toolbar}>
                                <button className={styles.applyBtn} onClick={sendApply}>发送申请</button>
                            </div>
                        </>
                    }
                </>
            )
        }
    </div>;
};

export default ApplyPermission;
